import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import type { MinimalAuthTokens } from '../types/auth'

type AuthenticationOptions = Parameters<typeof startAuthentication>[0]['optionsJSON']
type RegistrationOptions = Parameters<typeof startRegistration>[0]['optionsJSON']
export type PasskeyStatus = {
  locked: boolean
  passkeys: { id: string; createdAt: string; confirmed: boolean; supported: boolean; expiresAt?: string }[]
}
export type PasskeyProof = { password: string; secret: string }
export type PasskeyAction = 'createSwap' | 'approveSwap' | 'approveWhitelistedAddress'

function assertCeremony<T extends { challenge: string }>(
  ceremony: { options?: T; ceremonyId?: string } | null | undefined,
): asserts ceremony is { options: T; ceremonyId: string } {
  if (
    typeof ceremony?.options?.challenge !== 'string' || !ceremony.options.challenge ||
    typeof ceremony.ceremonyId !== 'string' || !ceremony.ceremonyId
  )
    throw new Error(
      'Passkey service is temporarily unavailable. Use your authenticator code and try again later.',
    )
}

export function passkeyError(error: unknown): string {
  if (error instanceof Error && ['NotAllowedError', 'AbortError'].includes(error.name))
    return 'Passkey cancelled or unavailable. Retry with the provider containing your registered passkey, or use your authenticator code.'
  return error instanceof Error ? error.message : 'Passkey operation failed. Try again.'
}

export function createPasskeyClient(apiUrl: string, admin = false) {
  const base = `${apiUrl.replace(/\/$/, '')}/authentication${admin ? '/admin' : ''}/passkeys`
  async function request<T>(path: string, token?: string, body?: object, signal?: AbortSignal): Promise<T> {
    const response = await fetch(base + path, {
      method: body ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    })
    if (!response.ok) throw new Error((await response.text()) || 'Passkey request failed')
    return response.json()
  }
  function supported() {
    if (!window.isSecureContext || !window.PublicKeyCredential)
      throw new Error(
        'Passkeys are unavailable in this browser. Use your authenticator code or a supported browser over HTTPS.',
      )
  }
  async function confirm(
    token: string,
    ceremony: { options: AuthenticationOptions; ceremonyId: string },
  ) {
    assertCeremony(ceremony)
    const response = await startAuthentication({ optionsJSON: ceremony.options })
    await request('/confirm/verify', token, { ceremonyId: ceremony.ceremonyId, response })
  }
  return {
    async enabled() {
      return (await request<{ enabled: boolean }>('/enabled')).enabled
    },
    status(token: string) {
      return request<PasskeyStatus>('', token)
    },
    async loginAvailable(username: string, password: string): Promise<boolean> {
      supported()
      const data = await request<{ hasPasskey: boolean }>('/login/options', undefined, { username, password }, AbortSignal.timeout(5000))
      return data?.hasPasskey === true
    },
    async login(username: string, password: string): Promise<MinimalAuthTokens> {
      supported()
      const data = await request<{
        hasPasskey: boolean
        options?: AuthenticationOptions
        ceremonyId: string
      }>('/login/options', undefined, { username, password })
      if (!data?.hasPasskey)
        throw new Error(
          'No registered passkey is available for this account. Use your authenticator code.',
        )
      assertCeremony(data)
      const response = await startAuthentication({ optionsJSON: data.options })
      return request<MinimalAuthTokens>('/login/verify', undefined, {
        ceremonyId: data.ceremonyId,
        response,
      })
    },
    async action(token: string, action: PasskeyAction): Promise<string> {
      supported()
      const data = await request<{
        hasPasskey: boolean
        options?: AuthenticationOptions
        ceremonyId?: string
      }>('/action/options', token, { action })
      if (!data.hasPasskey)
        throw new Error('No registered passkey is available for this account. Use your authenticator code.')
      assertCeremony(data)
      const response = await startAuthentication({ optionsJSON: data.options })
      const result = await request<{ approval: string }>('/action/verify', token, {
        ceremonyId: data.ceremonyId,
        response,
      })
      if (!result.approval) throw new Error('Passkey verification failed. Use your authenticator code.')
      return result.approval
    },
    async register(token: string, proof: PasskeyProof) {
      supported()
      const data = await request<{ options: RegistrationOptions; ceremonyId: string }>(
        '/register/options',
        token,
        proof,
      )
      assertCeremony(data)
      const response = await startRegistration({ optionsJSON: data.options })
      const pending = await request<{ options: AuthenticationOptions; ceremonyId: string }>(
        '/register/verify',
        token,
        { ceremonyId: data.ceremonyId, response },
      )
      await confirm(token, pending)
    },
    async retry(token: string, proof: PasskeyProof) {
      supported()
      await confirm(token, await request('/confirm/options', token, proof))
    },
    cancel(token: string, proof: PasskeyProof) {
      return request('/cancel', token, proof)
    },
    recover(
      token: string,
      proof: PasskeyProof,
      targetUserId: string,
      action: 'revoke' | 'reopen',
      identityVerificationReference: string,
    ) {
      return request('/recover', token, {
        ...proof,
        targetUserId,
        action,
        identityVerificationReference,
      })
    },
  }
}
